// src/resources/dashboards/ProductEvolution/sections/CustomersSection.tsx
//
// Base de clientes da rede: quem entrou, quem está ativo, quem esfriou.
//
// "Cadastrou e nunca abasteceu" fica em destaque porque é a conta de aquisição
// desperdiçada — número que ninguém olhava e que costuma ser a maior fatia da
// base. Os limiares de "em risco" e "inativo" vêm da régua de recuperação da
// PRÓPRIA rede quando ela existe, então são exibidos junto: sem isso, dois
// operadores comparam números que não querem dizer a mesma coisa.
import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import {
  COLORS, DeltaChip, EmptyHint, ErrorCard, SectionHeader, SkeletonCard, cardSx, fmtNum,
} from '../../shared';
import type { SectionState } from '../../shared';
import { ChainCustomersData } from '../types';
import { BackendNote, Card, MetricBar, Stat, StatGrid, bucketLabel, fmtDateTime, fmtPctSmall } from './kit';

const HEIGHT = 420;

const CustomersSection = ({ state }: { state: SectionState<ChainCustomersData> }) => {
  const theme = useTheme();
  if (state.loading && !state.data) return <SkeletonCard height={HEIGHT} />;
  if (state.error) return <ErrorCard onRetry={state.refetch} height={HEIGHT} />;
  if (!state.data) return null;

  const { totals, newCustomers, activeCustomers, lifecycle, series, bucketUnit } = state.data;
  const chartFont = theme.typography.fontFamily;

  const chartData = series.map(b => ({
    label: bucketLabel(b.bucket, bucketUnit),
    novos: b.newCustomers,
  }));

  const lifecycleTotal = lifecycle.active + lifecycle.atRisk + lifecycle.inactive;
  const lifecycleMax = Math.max(1, lifecycle.active, lifecycle.atRisk, lifecycle.inactive);

  return (
    <Box>
      <SectionHeader
        icon={<GroupsIcon sx={{ fontSize: 18 }} />}
        gradient={`linear-gradient(135deg, ${COLORS.teal}, ${COLORS.blue})`}
        title="Base de clientes"
        subtitle="Novos, ativos, em risco e inativos — e quanto da base nunca abasteceu"
      />

      <Box sx={{ display: 'grid', gap: 1.75 }}>
        <Box sx={cardSx}>
          <StatGrid min={158}>
            <Stat label="Base cadastrada" value={fmtNum(totals.base)} />
            <Stat label="Já abasteceram" value={fmtNum(totals.withFillin)} color={COLORS.success}
              sub={`ativação de ${fmtPctSmall(totals.activationRate)}`} />
            <Stat label="Nunca abasteceram" value={fmtNum(totals.neverFilled)} color={COLORS.warning}
              hint="Cadastro que não virou abastecimento: é a conta de aquisição que a rede pagou e não usou." />
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>
                Novos no período
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: COLORS.brand, lineHeight: 1.25 }}>
                {fmtNum(newCustomers.value)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <DeltaChip deltaPct={newCustomers.deltaPct} />
                <Typography sx={{ fontSize: 11, color: COLORS.muted }}>antes {fmtNum(newCustomers.previous)}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: COLORS.faint }}>
                Ativos no período
              </Typography>
              <Typography sx={{ fontSize: 22, fontWeight: 800, color: COLORS.blue, lineHeight: 1.25 }}>
                {fmtNum(activeCustomers.value)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <DeltaChip deltaPct={activeCustomers.deltaPct} />
                <Typography sx={{ fontSize: 11, color: COLORS.muted }}>antes {fmtNum(activeCustomers.previous)}</Typography>
              </Box>
            </Box>
          </StatGrid>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.75 }}>
          <Card
            title="Ciclo de vida da base"
            hint="Classificação pelo último abastecimento de cada cliente"
          >
            {!lifecycleTotal ? (
              <EmptyHint text="Nenhum cliente desta rede tem abastecimento registrado." />
            ) : (
              <>
                <MetricBar
                  label="Ativos"
                  value={`${fmtNum(lifecycle.active)} · ${fmtPctSmall(lifecycle.active / lifecycleTotal)}`}
                  pctOfMax={lifecycle.active / lifecycleMax} color={COLORS.success} labelWidth={110}
                />
                <MetricBar
                  label="Em risco"
                  value={`${fmtNum(lifecycle.atRisk)} · ${fmtPctSmall(lifecycle.atRisk / lifecycleTotal)}`}
                  pctOfMax={lifecycle.atRisk / lifecycleMax} color={COLORS.warning} labelWidth={110}
                />
                <MetricBar
                  label="Inativos"
                  value={`${fmtNum(lifecycle.inactive)} · ${fmtPctSmall(lifecycle.inactive / lifecycleTotal)}`}
                  pctOfMax={lifecycle.inactive / lifecycleMax} color={COLORS.danger} labelWidth={110}
                />
                <BackendNote>
                  Em risco = sem abastecer há mais de {fmtNum(lifecycle.thresholds.atRiskDays)} dias ·
                  {' '}inativo = há mais de {fmtNum(lifecycle.thresholds.inactiveDays)} dias.
                  {' '}Último abastecimento da rede: {fmtDateTime(lifecycle.lastFillinAt)}.
                </BackendNote>
              </>
            )}
          </Card>

          <Card title="Cadastros novos no tempo" hint={`Por ${bucketUnit === 'week' ? 'semana' : 'dia'}`}>
            {!chartData.length ? (
              <EmptyHint text="Nenhum cadastro novo no período." />
            ) : (
              <Box sx={{ height: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                      tickLine={false} axisLine={{ stroke: 'rgba(148,163,184,0.35)' }} minTickGap={16} />
                    <YAxis tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }}
                      tickLine={false} axisLine={false} width={44} tickFormatter={(v: number) => fmtNum(v)} />
                    <ReTooltip
                      cursor={{ fill: 'rgba(120,105,223,0.06)' }}
                      contentStyle={{ fontFamily: chartFont, fontSize: 12, borderRadius: 8, border: '1px solid rgba(120,105,223,0.2)' }}
                      formatter={(value: any, name: any) => [fmtNum(Number(value)), name]}
                    />
                    <Bar dataKey="novos" name="Cadastros novos" fill={COLORS.teal} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomersSection;
